import React, { useState, useEffect } from 'react';
import { PanelLeftClose, PanelLeftOpen, LogOut, GraduationCap, ShieldCheck, ChevronDown, ChevronRight, Search } from 'lucide-react';
import OkiruLogo from './OkiruLogo';
import AvatarImage from './AvatarImage';
import './Sidebar.css';

const COLLAPSE_KEY = 'okiru_sidebar_collapsed';
const GROUP_KEY = 'okiru_sidebar_group_';

const readPref = key => { try { return localStorage.getItem(key); } catch { return null; } };
const writePref = (key, value) => { try { localStorage.setItem(key, value); } catch { /* storage unavailable */ } };

// A collapsible nav group (e.g. Users, Courses). Children are
// { key, label, active, onSelect, hint }; `searchable` adds a filter box.
function NavGroup({ item, collapsed, onExpandSidebar }) {
  const [open, setOpen] = useState(() => readPref(GROUP_KEY + item.id) === '1');
  const [q, setQ] = useState('');
  const Icon = item.icon;
  const toggle = () => {
    if (collapsed) { onExpandSidebar(); setOpen(true); writePref(GROUP_KEY + item.id, '1'); return; }
    setOpen(o => { writePref(GROUP_KEY + item.id, o ? '0' : '1'); return !o; });
  };
  const term = q.trim().toLowerCase();
  const children = (item.children || []).filter(c => c.pinned || !term || c.label.toLowerCase().includes(term));
  const anyActive = (item.children || []).some(c => c.active);
  return (
    <div className="nav-group">
      <button className={`nav-item ${anyActive && !open ? 'nav-item--active' : ''}`} onClick={toggle} aria-expanded={open && !collapsed} title={collapsed ? item.label : undefined}>
        {Icon && <Icon size={18} strokeWidth={1.75} className="nav-icon" />}
        {!collapsed && <span className="nav-label">{item.label}</span>}
        {!collapsed && (open ? <ChevronDown size={14} className="nav-chevron" /> : <ChevronRight size={14} className="nav-chevron" />)}
      </button>
      {open && !collapsed && (
        <div className="nav-group-body">
          {item.searchable && (
            <div className="nav-group-search">
              <Search size={12} />
              <input type="search" placeholder={item.searchPlaceholder || 'Search'} value={q} onChange={e => setQ(e.target.value)} aria-label={item.searchPlaceholder || `Search ${item.label}`} />
            </div>
          )}
          <div className="nav-group-list">
            {children.map(c => (
              <button key={c.key} className={`nav-subitem ${c.active ? 'nav-subitem--active' : ''} ${c.pinned ? 'nav-subitem--pinned' : ''}`} onClick={c.onSelect} title={c.label}>
                <span className="nav-sublabel">{c.label}</span>
                {c.hint && <span className="nav-subhint">{c.hint}</span>}
              </button>
            ))}
            {children.length === 0 && <p className="nav-group-empty">{term ? 'No matches' : item.emptyText || 'Nothing here yet'}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ user, navItems, activeView, setActiveView, onLogout, isAdminView, canSwitchView, onToggleViewMode }) {
  const [collapsedPref, setCollapsedPref] = useState(() => readPref(COLLAPSE_KEY) === '1');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // Collapse is a desktop workspace feature — the mobile layout has its own
  // horizontal reflow, so a collapsed preference set on desktop never hides
  // labels once the viewport narrows.
  const collapsed = collapsedPref && !isMobile;
  const goProfile = () => setActiveView('profile');
  const toggleCollapsed = () => {
    setCollapsedPref(c => {
      writePref(COLLAPSE_KEY, !c ? '1' : '0');
      return !c;
    });
  };

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-logo-brand">
          <OkiruLogo height={26} className="sidebar-logo-img" surface="theme" symbol={collapsed} />
          {!collapsed && <span className="sidebar-logo-name">LEARN</span>}
        </div>
        {!collapsed && <span className="sidebar-logo-sub">{isAdminView ? 'Admin Portal' : user.role === 'facilitator' ? 'Facilitator Portal' : 'Learning Portal'}</span>}
      </div>
      <nav className="sidebar-nav" aria-label="Main">
        {!collapsed && <p className="nav-section-label">{isAdminView ? 'Administration' : 'My Learning'}</p>}
        {navItems.map(item => {
          if (item.children) return <NavGroup key={item.id} item={item} collapsed={collapsed} onExpandSidebar={toggleCollapsed} />;
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              className={`nav-item ${activeView === item.id ? 'nav-item--active' : ''}`}
              onClick={() => setActiveView(item.id)}
              title={collapsed ? item.label : undefined}
              aria-current={activeView === item.id ? 'page' : undefined}
              aria-label={collapsed ? item.label : undefined}
            >
              {Icon && <Icon size={18} strokeWidth={1.75} className="nav-icon" />}
              {!collapsed && <span className="nav-label">{item.label}</span>}
            </button>
          );
        })}
      </nav>
      {canSwitchView && (
        <button
          className="sidebar-view-toggle"
          onClick={onToggleViewMode}
          title={isAdminView ? 'Switch to student view' : 'Switch to admin view'}
        >
          {isAdminView ? <GraduationCap size={16} strokeWidth={1.75} /> : <ShieldCheck size={16} strokeWidth={1.75} />}
          {!collapsed && <span>{isAdminView ? 'View as student' : 'Back to admin'}</span>}
        </button>
      )}
      <button className="sidebar-collapse-btn" onClick={toggleCollapsed} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} aria-expanded={!collapsed}>
        {collapsed ? <PanelLeftOpen size={16} strokeWidth={1.75} /> : <PanelLeftClose size={16} strokeWidth={1.75} />}
      </button>
      <div className="sidebar-footer">
        <button className="sidebar-avatar-btn" onClick={goProfile} title="My Profile" aria-label="My profile">
          <AvatarImage
            src={user.avatar_url}
            className="user-avatar user-avatar--img"
            fallback={<div className="user-avatar">{user.avatar || user.name.slice(0,2).toUpperCase()}</div>}
          />
        </button>
        {!collapsed && (
          <button type="button" className="user-info" onClick={goProfile} aria-label={`My profile: ${user.name}`}>
            <span className="user-name">{user.name}</span>
            <span className="user-role">{user.company}</span>
          </button>
        )}
        <button className="logout-btn" onClick={onLogout} title="Sign out" aria-label="Sign out">
          <LogOut size={16} strokeWidth={1.75} aria-hidden="true" />
        </button>
      </div>
    </aside>
  );
}
